/**
 * AutoDiagnosisPanel.jsx — iter891 (Feb 2026)
 *
 * "🩺 Auto-suggested diagnoses across ALL uploaded labs".
 *
 * Group D / Item 14 from the 14-point feedback document. Aggregates
 * every canonical `lab_entries` value the user has uploaded and applies
 * international thresholds (ADA / ATA / AASLD / ESHRE / NASPGHAN /
 * NCEP-ATP III) to surface possible screening diagnoses in a SINGLE
 * unified list. Nothing is auto-confirmed — the user explicitly accepts
 * each suggestion, then it becomes part of `health_conditions` and
 * propagates to tissue zones + KEGG activity map + BHI Coach.
 *
 * Fetches: GET  /api/documents/auto-diagnosis/suggestions
 * Posts:   POST /api/documents/auto-diagnosis/accept
 */
import React, { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import DiscussBotDialog from "@/components/breast_intel/DiscussBotDialog";
import {
  Loader2, CheckCircle2, Stethoscope, Sparkles, ExternalLink,
  ShieldCheck, AlertTriangle, Brain, Layers, Activity, AlertOctagon,
  Users, Droplets, Apple, HeartPulse, X,
} from "lucide-react";
import { useAuth } from "@/App";
import { useTheme } from "@/theme/ThemeContext";
import { emitHealthEvent, useHealthEvent } from "@/utils/healthEvents";
import { toast } from "sonner";
import EducationalRibbon from "@/components/EducationalRibbon";
import MarkerReportButton from "@/components/MarkerReportButton";

const TX = {
  bg: {
    title: "🩺 Автоматично изведени диагнози от лабораторни данни",
    subtitle:
      "Обединяваме всичките Ви лабораторни маркери и прилагаме международни клинични прагове (ADA, ATA, AASLD, ESHRE, NCEP-ATP III). Всяко предложение изисква Вашето изрично потвърждение, преди да участва в BHI изчисленията.",
    loading: "Анализираме натрупаните лабораторни данни…",
    empty: "Няма нови автоматично изведени диагнози въз основа на текущите Ви данни.",
    alreadyAcceptedTitle: "Вече потвърдени",
    scanned: (n) => `Проверени: ${n} маркера`,
    accept: "Потвърди",
    acceptAll: "Потвърди всички",
    acceptSuccess: (n) => `Добавени са ${n} диагнози в анамнезата`,
    acceptError: "Грешка при потвърждаване",
    severity: { mild: "лека", moderate: "умерена", severe: "тежка" },
    evidence: "Основание",
    guideline: "Насоки",
    pathways: "KEGG пътища",
    safetyNote:
      "Забележка: това са сигнали от проверката, не диагноза. Потвърдете с Вашия лекар преди клинични решения.",
    // iter920 — LLM overlay & extended UX
    redFlagsTitle: "🚨 Спешни находки за преглед",
    patternClustersTitle: "🧩 Клинични модели по системи",
    differentialTitle: "🧠 Клинично моделиране на диференциална диагноза",
    systemSummaryTitle: "📊 Обобщение по системи",
    confidence: "Увереност",
    aiEducationalNote: "AI разпознава клинични модели през 60+ маркера. Всяка хипотеза е верифицирана със реалните Ви маркери, но НЕ е диагноза.",
    likelihoodBadge: { low: "нисък", moderate: "среден", high: "висок" },
    systemLabels: {
      metabolic: "Метаболитна", endocrine: "Ендокринна", inflammatory: "Възпалителна",
      hormonal: "Хормонална", hepatic: "Чернодробна", renal: "Бъбречна",
      cardiovascular: "Сърдечно-съдова", hematologic: "Хематологична",
      autoimmune: "Автоимунна", gynecologic: "Гинекологична",
      detoxification: "Детоксикация", neuroendocrine: "Невроендокринна",
      other: "Други",
    },
  },
  en: {
    title: "🩺 Auto-suggested diagnoses from your labs",
    subtitle:
      "We unify all your lab markers and apply international screening thresholds (ADA, ATA, AASLD, ESHRE, NCEP-ATP III). Every suggestion requires your explicit confirmation before it feeds BHI calculations.",
    loading: "Analysing your accumulated lab data…",
    empty: "No new auto-suggested diagnoses based on current lab data.",
    alreadyAcceptedTitle: "Already confirmed",
    scanned: (n) => `Scanned: ${n} markers`,
    accept: "Confirm",
    acceptAll: "Confirm all",
    acceptSuccess: (n) => `${n} diagnoses added to your history`,
    acceptError: "Error confirming",
    severity: { mild: "mild", moderate: "moderate", severe: "severe" },
    evidence: "Evidence",
    guideline: "Guideline",
    pathways: "KEGG pathways",
    safetyNote:
      "Note: these are screening signals, not a diagnosis. Please confirm with your clinician before any decisions.",
    redFlagsTitle: "🚨 Urgent findings to review",
    patternClustersTitle: "🧩 Cross-system pattern recognition (AI)",
    differentialTitle: "🧠 Clinical differential modelling",
    systemSummaryTitle: "📊 Summary by body system",
    confidence: "Confidence",
    aiEducationalNote: "AI recognises patterns across 60+ markers. Each hypothesis is verified against your actual markers, but is NOT a diagnosis.",
    likelihoodBadge: { low: "low", moderate: "moderate", high: "high" },
    systemLabels: {
      metabolic: "Metabolic", endocrine: "Endocrine", inflammatory: "Inflammatory",
      hormonal: "Hormonal", hepatic: "Hepatic", renal: "Renal",
      cardiovascular: "Cardiovascular", hematologic: "Hematologic",
      autoimmune: "Autoimmune", gynecologic: "Gynecologic",
      detoxification: "Detoxification", neuroendocrine: "Neuroendocrine",
      other: "Other",
    },
  },
};

const SEVERITY_COLOR = {
  mild: "bg-yellow-500/20 text-yellow-700 dark:text-yellow-300",
  moderate: "bg-orange-500/20 text-orange-700 dark:text-orange-300",
  severe: "bg-red-500/20 text-red-700 dark:text-red-300",
};

const LIKELIHOOD_COLOR = {
  low:      "bg-slate-400/25 text-slate-700 dark:text-slate-200",
  moderate: "bg-amber-500/25 text-amber-800 dark:text-amber-200",
  high:     "bg-rose-500/25 text-rose-800 dark:text-rose-200",
};

// iter927 — Map body system → specialist bot for the "Discuss" button on
// Claude differentials (which don't carry a specialist_bot assignment).
// Used only for routing / dialog header; the actual `query/execute` picks
// its own agent.
const _SYSTEM_TO_BOT = {
  endocrine:      "endobot",
  metabolic:      "endobot",
  hormonal:       "endobot",
  neuroendocrine: "endobot",
  hematologic:    "hemobot",
  renal:          "nephrobot",
  hepatic:        "nutribot",
  inflammatory:   "nutribot",
  autoimmune:     "endobot",
  cardiovascular: "nutribot",
  gynecologic:    "endobot",
  detoxification: "nutribot",
};
const _BOT_LABEL_BG = {
  endobot: "EndoBot", hemobot: "HemoBot", nephrobot: "NephroBot", nutribot: "NutriBot", ai: "AI",
};
function _systemToBot(system) {
  const k = (system || "").toLowerCase();
  return _SYSTEM_TO_BOT[k] || "ai";
}
function _systemToBotLabel(system) {
  return _BOT_LABEL_BG[_systemToBot(system)] || "AI";
}

// iter920 — small confidence bar (0-100)
function ConfidenceBar({ value = 0, isDark }) {
  const pct = Math.max(0, Math.min(100, Number(value) || 0));
  const barColor =
    pct >= 75 ? "from-rose-500 to-orange-500" :
    pct >= 55 ? "from-amber-500 to-yellow-500" :
                "from-emerald-500 to-teal-500";
  return (
    <div className={`w-full h-1.5 rounded-full overflow-hidden ${isDark ? "bg-white/10" : "bg-gray-200"}`}
         data-testid="auto-diagnosis-confidence-bar">
      <div className={`h-full bg-gradient-to-r ${barColor}`} style={{ width: `${pct}%` }} />
    </div>
  );
}

export default function AutoDiagnosisPanel({ lang = "bg", compact = false }) {
  const { API } = useAuth();
  const { isDark } = useTheme();
  const t = TX[lang] || TX.bg;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  // iter927 — Inline "Discuss with Bot" dialog state
  const [discussOpen, setDiscussOpen] = useState(false);
  const [discussInsight, setDiscussInsight] = useState(null);
  const [discussPreQuery, setDiscussPreQuery] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: res } = await axios.get(`${API}/documents/auto-diagnosis/suggestions`);
      setData(res);
    } catch (e) {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [API]);

  useEffect(() => { load(); }, [load]);

  // iter920 — Live refresh when a new lab upload finishes.
  useHealthEvent("auto-diagnosis-snapshot-ready", (detail) => {
    load();
    if (detail?.count > 0) {
      toast.success(`Нови ${detail.count} автоматични подсказки от последния документ.`);
    }
  });

  // iter923 — Manual "refresh AI analysis" — kicks the background pipeline
  // (specialist bots + Claude LLM), polls the snapshot endpoint until we see
  // an updated generated_at, then reloads.
  const [refreshing, setRefreshing] = useState(false);
  // iter929 — Streaming visualization state: which bots are currently working
  // and elapsed seconds. Populated during refresh so the UI shows "🤖 EndoBot
  // анализира…" pills with a live progress indicator.
  const [streamingBots, setStreamingBots] = useState([]);
  const [streamingSeconds, setStreamingSeconds] = useState(0);
  const refreshAI = useCallback(async () => {
    if (refreshing) return;
    setRefreshing(true);
    // Guess which bots WILL run from current findings (best-effort UX):
    // check the snapshot's already-known markers and light up the
    // corresponding specialty pills. If snapshot is empty, show all 4.
    const activeMarkerHint = new Set(
      (data?.suggestions || []).flatMap(s => (s.evidence || []).map(e => e.code || ""))
    );
    const _HEMO = ["hemoglobin","wbc","rbc","platelets","mcv","lymphocyte_pct","ferritin","hs_crp","esr"];
    const _ENDO = ["hba1c","fasting_glucose","fasting_insulin","tsh","ft3","ft4","estradiol_serum","progesterone","testosterone","dhea_s","total_cholesterol","ldl_cholesterol","triglycerides"];
    const _NEPH = ["creatinine","urea","egfr","uric_acid","sodium","potassium"];
    const bots = [];
    if (_HEMO.some(m => activeMarkerHint.has(m))) bots.push("hemobot");
    if (_ENDO.some(m => activeMarkerHint.has(m))) bots.push("endobot");
    if (_NEPH.some(m => activeMarkerHint.has(m))) bots.push("nephrobot");
    if (bots.length > 0) bots.push("nutribot");
    setStreamingBots(bots.length ? bots : ["endobot","hemobot","nephrobot","nutribot"]);
    setStreamingSeconds(0);
    const secTicker = setInterval(() => setStreamingSeconds((s) => s + 1), 1000);
    toast.info("AI анализът се генерира във фонов режим (30-60 сек).");
    try {
      await axios.post(`${API}/documents/auto-diagnosis/snapshot/refresh`, {});
      // Poll for snapshot up to 90s
      const startTs = (data?.generated_at || "");
      let tries = 0;
      const poll = async () => {
        tries += 1;
        try {
          const snap = await axios.get(`${API}/documents/auto-diagnosis/snapshot/latest`);
          const newTs = snap.data?.snapshot?.generated_at || "";
          if (newTs && newTs !== startTs) {
            await load();
            toast.success("AI анализът е готов.");
            clearInterval(secTicker);
            setRefreshing(false);
            setStreamingBots([]);
            setStreamingSeconds(0);
            return;
          }
        } catch (err) { /* poll again */ }
        if (tries < 18) {
          setTimeout(poll, 5000);
        } else {
          clearInterval(secTicker);
          setRefreshing(false);
          setStreamingBots([]);
          setStreamingSeconds(0);
          toast.warning("AI анализът все още се строи — обновете страницата след минута.");
        }
      };
      setTimeout(poll, 3000);
    } catch (err) {
      clearInterval(secTicker);
      toast.error("Грешка при заявяване на AI анализ.");
      setRefreshing(false);
      setStreamingBots([]);
      setStreamingSeconds(0);
    }
  }, [refreshing, data, API, load]);

  // iter927 — "Discuss with Bot" now opens an INLINE dialog instead of
  // navigating to the Precision Health AI chat. The user asked for a
  // dialog because the previous nav flow was confusing (button said
  // "Ask AI section" but never explained where that was).
  const dispatchingRef = useRef(false);
  const discussWithBot = useCallback((ins, ev) => {
    if (ev && ev.stopPropagation) ev.stopPropagation();
    if (ev && ev.preventDefault) ev.preventDefault();
    if (!ins) return;
    if (dispatchingRef.current) return;              // debounce duplicate clicks
    dispatchingRef.current = true;
    setTimeout(() => { dispatchingRef.current = false; }, 1500);

    const markerList = (ins.supporting_markers || []).join(", ");
    const pathwayList = (ins.pathways || []).map((p) => p.name_bg || p.id).join(", ");
    const vaultLine = ins.has_vault_convergence
      ? "\n(Пътищата отбелязани с ★ вече са активни в моя Pathway Vault от други състояния.)"
      : "";
    const preQuery =
      `Здравей, ${ins.bot_name || ins.bot}. От моя auto-diagnosis отчет ти повдигна следния клиничен модел:\n\n` +
      `**${ins.name_bg}** (вероятност: ${ins.likelihood || "moderate"})\n` +
      `Обосновка: ${ins.rationale}\n` +
      `Маркери: ${markerList}\n` +
      (pathwayList ? `KEGG пътища: ${pathwayList}\n` : "") +
      vaultLine +
      `\nМоже ли да ми обясниш по-подробно КАК тези маркери водят до този клиничен модел, ` +
      `и какво КОНКРЕТНО да обсъдя с моя лекар?`;

    setDiscussInsight(ins);
    setDiscussPreQuery(preQuery);
    setDiscussOpen(true);
  }, []);

  const acceptConds = async (ids) => {
    if (!ids || ids.length === 0) return;
    setBusy(ids.length === 1 ? ids[0] : "all");
    try {
      const { data: res } = await axios.post(
        `${API}/documents/auto-diagnosis/accept`,
        { condition_ids: ids },
      );
      toast.success(t.acceptSuccess(res.count || 0));
      emitHealthEvent("conditions-changed", { source: "auto-diagnosis-accept" });
      await load();
    } catch (e) {
      toast.error(t.acceptError);
    } finally {
      setBusy(null);
    }
  };

  // iter933 — "Откажи" (reject) support. Persists a per-user dismissal
  // so the suggestion never reappears without a fresh upload. Server
  // stores it in `auto_diagnosis_rejections` with (user_id, condition_id).
  const rejectConds = async (ids, reason = "") => {
    if (!ids || ids.length === 0) return;
    if (!window.confirm("Сигурни ли сте, че искате да отхвърлите тази диагностична подсказка? Няма да я видите отново.")) return;
    setBusy(ids.length === 1 ? ids[0] + "_rej" : "all_rej");
    try {
      const { data: res } = await axios.post(
        `${API}/documents/auto-diagnosis/reject`,
        { condition_ids: ids, reason },
      );
      toast.success(`Отхвърлени: ${res.count || ids.length}`);
      emitHealthEvent("conditions-changed", { source: "auto-diagnosis-reject" });
      await load();
    } catch (e) {
      toast.error("Грешка при отхвърляне. Опитайте пак.");
    } finally {
      setBusy(null);
    }
  };

  if (loading) {
    return (
      <div className="p-3 text-xs opacity-70 flex items-center gap-2" data-testid="auto-diagnosis-loading">
        <Loader2 className="w-3 h-3 animate-spin" /> {t.loading}
      </div>
    );
  }
  if (!data) return null;

  const suggestions = Array.isArray(data.suggestions) ? data.suggestions : [];
  const alreadyAccepted = Array.isArray(data.already_accepted) ? data.already_accepted : [];
  const sourcesConsidered = data.sources_considered || 0;
  const hasAny = suggestions.length > 0;

  return (
    <div
      className={`p-4 rounded-2xl border ${
        isDark ? "border-white/10 bg-white/5" : "border-gray-200 bg-white"
      }`}
      data-testid="auto-diagnosis-panel"
    >
      {/* ── Header ────────────────────────────────────────── */}
      {/* iter973 — Header restructured for mobile: title row on top,
          action buttons stack below on narrow viewports (flex-col →
          flex-row on ≥sm). Buttons stretch full-width on mobile so
          they don't overflow the container. */}
      <div className="mb-3">
        <div className="flex items-start gap-2 mb-2">
          <div className={`shrink-0 w-9 h-9 rounded-xl grid place-items-center ${
            isDark ? "bg-emerald-500/20 text-emerald-300" : "bg-emerald-100 text-emerald-700"
          }`}>
            <Stethoscope className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-extrabold" data-testid="auto-diagnosis-title">{t.title}</div>
            {!compact && (
              <p className="text-[11px] opacity-70 leading-relaxed mt-1">{t.subtitle}</p>
            )}
            <div className="text-[10px] opacity-60 mt-1" data-testid="auto-diagnosis-scanned">
              {t.scanned(sourcesConsidered)}
            </div>
          </div>
        </div>
        {(hasAny || true) && (
          <div className="flex flex-col sm:flex-row gap-2">
            {hasAny && suggestions.length > 1 && (
              <button
                onClick={() => acceptConds(suggestions.map((s) => s.condition_id))}
                disabled={busy !== null}
                className="flex-1 sm:flex-none justify-center px-3 py-2 rounded-lg text-[12px] font-bold text-white bg-gradient-to-r from-emerald-500 to-teal-500 hover:opacity-90 disabled:opacity-60 flex items-center gap-1.5"
                data-testid="auto-diagnosis-accept-all"
              >
                {busy === "all" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                {t.acceptAll}
              </button>
            )}
            <button
              onClick={refreshAI}
              disabled={refreshing}
              title="Пусни специалист bots (EndoBot / HemoBot / NephroBot / NutriBot) + Claude AI за нов задълбочен анализ"
              className={`flex-1 sm:flex-none justify-center px-3 py-2 rounded-lg text-[12px] font-bold flex items-center gap-1.5 ${
                isDark ? "bg-cyan-500/20 text-cyan-300 hover:bg-cyan-500/30" : "bg-cyan-100 text-cyan-700 hover:bg-cyan-200"
              } disabled:opacity-50`}
              data-testid="auto-diagnosis-refresh-ai-btn"
            >
              {refreshing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
              {refreshing ? "Bots работят…" : "🤖 Обнови AI анализ"}
            </button>
          </div>
        )}
      </div>

      {/* iter929 — Streaming visualization: shows which bots are actively
          analysing during the async refresh. Each bot gets a pill with
          a pulsing indicator + elapsed timer. Auto-hides when done. */}
      {refreshing && streamingBots.length > 0 && (
        <div
          className={`mb-3 p-3 rounded-xl border ${
            isDark ? "border-cyan-400/30 bg-cyan-500/5" : "border-cyan-200 bg-gradient-to-r from-cyan-50 to-sky-50"
          }`}
          data-testid="auto-diagnosis-streaming"
        >
          <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
            <div className="flex items-center gap-1.5">
              <Loader2 className="w-3.5 h-3.5 animate-spin text-cyan-600" />
              <span className={`text-[11px] font-extrabold ${isDark ? "text-cyan-300" : "text-cyan-700"}`}>
                Специализираният здравен анализ обработва данните Ви…
              </span>
            </div>
            <span className={`text-[10px] tabular-nums font-mono ${isDark ? "text-cyan-300/70" : "text-cyan-600/80"}`}>
              {streamingSeconds}с / ~45с
            </span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {streamingBots.map((bk) => {
              const Icon = { endobot: Activity, hemobot: Droplets, nephrobot: HeartPulse, nutribot: Apple }[bk] || Users;
              const label = { endobot: "EndoBot", hemobot: "HemoBot", nephrobot: "NephroBot", nutribot: "NutriBot" }[bk] || bk;
              return (
                <span
                  key={bk}
                  className={`text-[10px] px-2 py-1 rounded-full font-bold flex items-center gap-1.5 ${
                    isDark ? "bg-cyan-500/15 text-cyan-200 border border-cyan-400/30"
                           : "bg-white text-cyan-800 border border-cyan-300 shadow-sm"
                  }`}
                  data-testid={`streaming-bot-${bk}`}
                >
                  <span className="relative flex h-1.5 w-1.5">
                    <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${isDark ? "bg-cyan-400" : "bg-cyan-500"}`}></span>
                    <span className={`relative inline-flex rounded-full h-1.5 w-1.5 ${isDark ? "bg-cyan-300" : "bg-cyan-600"}`}></span>
                  </span>
                  <Icon className="w-2.5 h-2.5" />
                  {label}
                </span>
              );
            })}
          </div>
          <div className={`mt-2 text-[9px] opacity-70 italic ${isDark ? "text-cyan-200/70" : "text-cyan-800/70"}`}>
            Всеки специализиран модул се позовава на клинична литература и проверява хипотезите срещу Вашите реални маркери.
          </div>
        </div>
      )}

      {/* ── iter920 — LLM overlay: red flags FIRST (urgent) ── */}
      {Array.isArray(data.llm_overlay?.red_flags) && data.llm_overlay.red_flags.length > 0 && (
        <div className={`mb-3 p-3 rounded-xl border-l-4 border-rose-500 ${
          isDark ? "bg-rose-500/10" : "bg-rose-50"
        }`} data-testid="auto-diagnosis-red-flags">
          <div className="flex items-center gap-2 mb-2">
            <AlertOctagon className="w-4 h-4 text-rose-500" />
            <span className="text-xs font-extrabold text-rose-700 dark:text-rose-300">
              {t.redFlagsTitle}
            </span>
          </div>
          <ul className="space-y-1.5">
            {data.llm_overlay.red_flags.map((rf, i) => (
              <li key={i} className="text-[11px] leading-relaxed" data-testid={`red-flag-${i}`}>
                <span className="font-mono font-bold">{rf.marker}={rf.value}</span>
                {" — "}
                {rf.reason_bg || rf.reason || ""}
                {rf.action_bg && (
                  <div className="text-[10px] opacity-80 mt-0.5 italic">→ {rf.action_bg}</div>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* ── iter928 — BOTS-FIRST: Specialist Bot Insights are now the PRIMARY layer ── */}
      {Array.isArray(data.specialist_bots?.insights) && data.specialist_bots.insights.length > 0 && (
        <div className={`mb-3 p-3 rounded-xl border-2 ${
          isDark ? "border-cyan-400/50 bg-cyan-500/10" : "border-cyan-300 bg-cyan-50"
        }`} data-testid="auto-diagnosis-specialist-bots">
          <div className="flex items-center gap-2 mb-2">
            <Users className="w-4 h-4 text-cyan-500" />
            <span className="text-xs font-extrabold text-cyan-700 dark:text-cyan-300">
              🧬 Специализиран здравен анализ ({data.specialist_bots.bots_invoked.join(" · ")})
            </span>
          </div>
          <ul className="space-y-2">
            {data.specialist_bots.insights.map((ins, i) => {
              const botIcon = {
                endobot: Activity, hemobot: Droplets,
                nephrobot: HeartPulse, nutribot: Apple,
              }[ins.bot] || Users;
              const BotIcon = botIcon;
              return (
                <li key={ins.condition_id + i} className="text-[11px] leading-relaxed"
                    data-testid={`specialist-insight-${ins.bot}-${i}`}>
                  <div className="flex items-center gap-2 flex-wrap mb-0.5">
                    <BotIcon className="w-3 h-3 text-cyan-600" />
                    <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-bold ${
                      isDark ? "bg-cyan-500/25" : "bg-cyan-100"
                    }`}>
                      {ins.bot_name || ins.bot}
                    </span>
                    <span className="font-bold">{ins.name_bg}</span>
                    {ins.likelihood && (
                      <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-bold ${
                        LIKELIHOOD_COLOR[ins.likelihood] || LIKELIHOOD_COLOR.moderate
                      }`}>
                        {t.likelihoodBadge[ins.likelihood] || ins.likelihood}
                      </span>
                    )}
                    {ins.has_vault_convergence && (
                      <span className="text-[8px] px-1.5 py-0.5 rounded-full font-bold bg-fuchsia-500/25 text-fuchsia-700 dark:text-fuchsia-300"
                            title="Един или повече пътища от този hypothesis са ВЕЧЕ активни във вашия Pathway Vault">
                        🔥 Vault convergence
                      </span>
                    )}
                  </div>
                  <div className="opacity-80">{ins.rationale}</div>
                  {ins.action_bg && (
                    <div className="mt-1 text-[10.5px] leading-relaxed text-cyan-600 dark:text-cyan-300"
                      data-testid={`auto-diagnosis-bot-action-${ins.condition_id || ins.bot}`}>
                      <span className="font-bold">Какво да направя сега: </span>{ins.action_bg}
                    </div>
                  )}
                  {Array.isArray(ins.supporting_markers) && ins.supporting_markers.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {ins.supporting_markers.slice(0, 6).map((m, mi) => (
                        <span key={mi} className={`text-[9px] px-1.5 py-0.5 rounded-md font-mono ${
                          isDark ? "bg-white/5" : "bg-white border border-cyan-200"
                        }`}>{m}</span>
                      ))}
                    </div>
                  )}
                  {/* KEGG pathway chips with Vault highlight */}
                  {Array.isArray(ins.pathways) && ins.pathways.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1.5" data-testid={`insight-pathways-${i}`}>
                      {ins.pathways.map((pw, pi) => (
                        <span key={pi}
                              title={pw.kegg ? `KEGG: ${pw.kegg}` : ""}
                              className={`text-[9px] px-1.5 py-0.5 rounded-md font-mono ${
                                pw.in_vault
                                  ? (isDark ? "bg-fuchsia-500/30 text-fuchsia-200 ring-1 ring-fuchsia-400"
                                            : "bg-fuchsia-100 text-fuchsia-800 ring-1 ring-fuchsia-400")
                                  : (isDark ? "bg-white/[0.03] text-white/70"
                                            : "bg-gray-100 text-gray-600")
                              }`}>
                          {pw.in_vault && "★ "}{pw.name_bg}
                        </span>
                      ))}
                    </div>
                  )}
                  {ins.next_step_bg && (
                    <div className="text-[9px] opacity-70 italic mt-0.5">→ {ins.next_step_bg}</div>
                  )}
                  {/* "Discuss with [Bot]" — opens inline DiscussBotDialog */}
                  <button
                    type="button"
                    onClick={(ev) => discussWithBot(ins, ev)}
                    className={`mt-1.5 text-[10px] px-2 py-1 rounded-md font-bold flex items-center gap-1 ${
                      isDark ? "bg-cyan-500/20 text-cyan-300 hover:bg-cyan-500/30"
                             : "bg-cyan-100 text-cyan-700 hover:bg-cyan-200"
                    }`}
                    data-testid={`discuss-with-bot-${ins.bot}-${i}`}
                  >
                    <BotIcon className="w-2.5 h-2.5" />
                    Обсъди с {ins.bot_name || ins.bot}
                  </button>
                </li>
              );
            })}
          </ul>
          <div className="mt-2 text-[9px] opacity-60 italic border-t border-dashed border-current/20 pt-1.5">
            Специализираният здравен анализ се позовава на професионална клинична литература. Всяка хипотеза е верифицирана срещу реалните Ви маркери. Образователна информация, не диагноза.
          </div>
        </div>
      )}

      {/* ── iter928 — "Supporting reasoning" section header shown when bots layer is primary ── */}
      {(Array.isArray(data.specialist_bots?.insights) && data.specialist_bots.insights.length > 0) &&
       (Array.isArray(data.llm_overlay?.pattern_clusters) && data.llm_overlay.pattern_clusters.length > 0 ||
        Array.isArray(data.llm_overlay?.differential) && data.llm_overlay.differential.length > 0) && (
        <div className={`text-[10px] uppercase tracking-[0.14em] font-bold mt-4 mb-2 opacity-60 ${isDark ? "text-white" : "text-gray-500"}`}
             data-testid="supporting-reasoning-header">
          Допълващ клиничен преглед (общ модел + правила)
        </div>
      )}

      {/* ── iter920 — Pattern clusters ── */}
      {Array.isArray(data.llm_overlay?.pattern_clusters) && data.llm_overlay.pattern_clusters.length > 0 && (
        <div className={`mb-3 p-3 rounded-xl border ${
          isDark ? "border-indigo-400/20 bg-indigo-500/5" : "border-indigo-200 bg-indigo-50"
        }`} data-testid="auto-diagnosis-pattern-clusters">
          <div className="flex items-center gap-2 mb-2">
            <Layers className="w-4 h-4 text-indigo-500" />
            <span className="text-xs font-extrabold text-indigo-700 dark:text-indigo-300">
              {t.patternClustersTitle}
            </span>
          </div>
          <ul className="space-y-2">
            {data.llm_overlay.pattern_clusters.map((c, i) => (
              <li key={c.cluster_id || i} className="text-[11px] leading-relaxed" data-testid={`pattern-cluster-${i}`}>
                <div className="flex items-center gap-2 flex-wrap mb-0.5">
                  <span className="font-bold">{c.name_bg}</span>
                  {c.likelihood && (
                    <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-bold ${
                      LIKELIHOOD_COLOR[c.likelihood] || LIKELIHOOD_COLOR.moderate
                    }`}>
                      {t.likelihoodBadge[c.likelihood] || c.likelihood}
                    </span>
                  )}
                </div>
                <div className="opacity-80">{c.rationale}</div>
                {Array.isArray(c.supporting_markers) && c.supporting_markers.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1">
                    {c.supporting_markers.slice(0, 6).map((m, mi) => (
                      <span key={mi} className={`text-[9px] px-1.5 py-0.5 rounded-md font-mono ${
                        isDark ? "bg-white/5" : "bg-white border border-indigo-200"
                      }`}>{m}</span>
                    ))}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* ── iter920 — Differential (AI hypotheses) ── */}
      {Array.isArray(data.llm_overlay?.differential) && data.llm_overlay.differential.length > 0 && (
        <div className={`mb-3 p-3 rounded-xl border ${
          isDark ? "border-purple-400/20 bg-purple-500/5" : "border-purple-200 bg-purple-50"
        }`} data-testid="auto-diagnosis-differential">
          <div className="flex items-center gap-2 mb-2">
            <Brain className="w-4 h-4 text-purple-500" />
            <span className="text-xs font-extrabold text-purple-700 dark:text-purple-300">
              {t.differentialTitle}
            </span>
          </div>
          <ul className="space-y-2">
            {data.llm_overlay.differential.map((d, i) => (
              <li key={d.condition_id || i} className="text-[11px] leading-relaxed" data-testid={`differential-${i}`}>
                <div className="flex items-center gap-2 flex-wrap mb-0.5">
                  <span className="font-bold">{d.name_bg}</span>
                  {d.likelihood && (
                    <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-bold ${
                      LIKELIHOOD_COLOR[d.likelihood] || LIKELIHOOD_COLOR.moderate
                    }`}>
                      {t.likelihoodBadge[d.likelihood] || d.likelihood}
                    </span>
                  )}
                </div>
                <div className="opacity-80">{d.rationale}</div>
                {d.guideline_ref && (
                  <div className="text-[9px] opacity-60 italic mt-0.5">ref: {d.guideline_ref}</div>
                )}
                <div className="flex flex-wrap gap-1.5 mt-1.5">
                  {/* iter925 — Accept differential hypothesis → wires it to
                      health_conditions → Vault → BHI recalculates. */}
                  {d.condition_id && (
                    <>
                      <button
                        type="button"
                        onClick={() => acceptConds([d.condition_id])}
                        disabled={busy !== null}
                        className={`text-[10px] px-2 py-1 rounded-md font-bold flex items-center gap-1 ${
                          isDark ? "bg-purple-500/20 text-purple-300 hover:bg-purple-500/30"
                                 : "bg-purple-100 text-purple-700 hover:bg-purple-200"
                        } disabled:opacity-50`}
                        data-testid={`accept-differential-${i}`}
                      >
                        <CheckCircle2 className="w-2.5 h-2.5" />
                        Приеми и активирай KEGG пътища
                      </button>
                      {/* iter933 — Reject a Claude differential */}
                      <button
                        type="button"
                        onClick={() => rejectConds([d.condition_id])}
                        disabled={busy !== null}
                        className={`text-[10px] px-2 py-1 rounded-md font-bold flex items-center gap-1 ${
                          isDark ? "bg-slate-500/20 text-slate-300 hover:bg-slate-500/30"
                                 : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                        } disabled:opacity-50`}
                        data-testid={`reject-differential-${i}`}
                        title="Отхвърли — няма да я видите отново"
                      >
                        <X className="w-2.5 h-2.5" />
                        Откажи
                      </button>
                    </>
                  )}
                  {/* iter927 — "Обсъди" button on differentials too, so users
                      can always drill into a hypothesis even when specialist
                      bots didn't run (e.g. no vector-store hit). Routes to the
                      most relevant specialist based on the differential's
                      body system; falls back to a generic AI. */}
                  <button
                    type="button"
                    onClick={(ev) => discussWithBot({
                      condition_id:      d.condition_id,
                      name_bg:           d.name_bg,
                      rationale:         d.rationale,
                      likelihood:        d.likelihood || "moderate",
                      supporting_markers: d.supporting_markers || [],
                      pathways:          d.pathways || [],
                      bot:               d.bot || _systemToBot(d.system) || "ai",
                      bot_name:          d.bot_name || _systemToBotLabel(d.system),
                      has_vault_convergence: !!d.has_vault_convergence,
                    }, ev)}
                    className={`text-[10px] px-2 py-1 rounded-md font-bold flex items-center gap-1 ${
                      isDark ? "bg-cyan-500/20 text-cyan-300 hover:bg-cyan-500/30"
                             : "bg-cyan-100 text-cyan-700 hover:bg-cyan-200"
                    }`}
                    data-testid={`discuss-differential-${i}`}
                  >
                    💬 Обсъди с {_systemToBotLabel(d.system)}
                  </button>
                </div>
              </li>
            ))}
          </ul>
          <div className="mt-2 text-[9px] opacity-60 italic border-t border-dashed border-current/20 pt-1.5">
            {t.aiEducationalNote}
          </div>
        </div>
      )}

      {/* ── iter920 — System summary chips ── */}
      {data.llm_overlay?.system_summary && Object.keys(data.llm_overlay.system_summary).length > 0 && (
        <div className={`mb-3 p-3 rounded-xl ${isDark ? "bg-white/[0.03]" : "bg-gray-50"}`}
             data-testid="auto-diagnosis-system-summary">
          <div className="flex items-center gap-2 mb-2">
            <Activity className="w-4 h-4 text-teal-500" />
            <span className="text-xs font-extrabold">{t.systemSummaryTitle}</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
            {Object.entries(data.llm_overlay.system_summary).map(([k, v]) => (
              <div key={k} className="text-[10px] leading-relaxed">
                <span className="font-bold opacity-80">
                  {t.systemLabels[k] || k}:
                </span>{" "}
                <span className="opacity-70">{v}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* iter928 — Specialist Bots block moved higher (now primary layer,
          right after red-flags). This is the OLD position; kept blank. */}

      {/* ── Empty state ───────────────────────────────────── */}
      {!hasAny ? (
        <div className="py-6 text-center" data-testid="auto-diagnosis-empty">
          <ShieldCheck className={`w-8 h-8 mx-auto mb-2 ${isDark ? "text-emerald-400/60" : "text-emerald-500/60"}`} />
          <div className="text-xs opacity-70">{t.empty}</div>
        </div>
      ) : (
        <ul className="grid grid-cols-1 min-[420px]:grid-cols-2 gap-2">
          {suggestions.map((s) => {
            const busyRow = busy === s.condition_id;
            return (
              <li
                key={s.condition_id}
                className={`p-2.5 sm:p-3 rounded-xl border ${
                  isDark ? "border-white/10 bg-white/[0.03]" : "border-gray-200 bg-gray-50"
                }`}
                data-testid={`auto-diagnosis-item-${s.condition_id}`}
              >
                <div className="flex items-start justify-between gap-2 mb-1.5">
                  <div className="flex items-center gap-2 flex-wrap min-w-0">
                    <Sparkles className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                    <span className="text-[12px] font-extrabold truncate" data-testid={`auto-diagnosis-name-${s.condition_id}`}>
                      {s.name_bg}
                    </span>
                    <span
                      className={`text-[9px] px-1.5 py-0.5 rounded-full font-bold ${
                        SEVERITY_COLOR[s.severity] || SEVERITY_COLOR.mild
                      }`}
                      data-testid={`auto-diagnosis-sev-${s.condition_id}`}
                    >
                      {t.severity[s.severity] || s.severity}
                    </span>
                    {s.guideline && (
                      <span className="text-[9px] opacity-60 font-medium">
                        · {t.guideline}: {s.guideline}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      onClick={() => acceptConds([s.condition_id])}
                      disabled={busy !== null}
                      className="px-2 py-1 rounded-md text-[10px] font-bold bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-700 dark:text-emerald-300 disabled:opacity-60 flex items-center gap-1"
                      data-testid={`auto-diagnosis-accept-${s.condition_id}`}
                    >
                      {busyRow ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
                      {t.accept}
                    </button>
                    {/* iter933 — Reject button. Persists dismissal per user. */}
                    <button
                      onClick={() => rejectConds([s.condition_id])}
                      disabled={busy !== null}
                      className="px-2 py-1 rounded-md text-[10px] font-bold bg-slate-500/15 hover:bg-slate-500/25 text-slate-600 dark:text-slate-300 disabled:opacity-60 flex items-center gap-1"
                      data-testid={`auto-diagnosis-reject-${s.condition_id}`}
                      title="Отхвърли — няма да я видите отново"
                    >
                      <X className="w-3 h-3" />
                      Откажи
                    </button>
                  </div>
                </div>

                <div className="text-[11px] leading-relaxed opacity-80 mb-1.5">
                  {s.reason}
                </div>

                {/* iter1013 — какво да направя сега (на прост език) */}
                {s.action_bg && (
                  <div className={`text-[11px] leading-relaxed mb-2 p-2 rounded-lg border-l-[3px] border-cyan-500 ${
                    isDark ? "bg-cyan-500/[0.08]" : "bg-cyan-50"}`}
                    data-testid={`auto-diagnosis-action-${s.condition_id}`}>
                    <span className="font-bold text-cyan-600 dark:text-cyan-300">
                      Какво да направя сега:{" "}
                    </span>
                    {s.action_bg}
                  </div>
                )}

                {/* iter920 — Confidence bar */}
                {typeof s.confidence === "number" && (
                  <div className="mb-2">
                    <div className="flex items-center justify-between text-[9px] opacity-60 mb-0.5">
                      <span>{t.confidence}</span>
                      <span className="font-bold" data-testid={`auto-diagnosis-conf-${s.condition_id}`}>
                        {s.confidence}%
                      </span>
                    </div>
                    <ConfidenceBar value={s.confidence} isDark={isDark} />
                  </div>
                )}

                {/* Evidence markers */}
                {Array.isArray(s.evidence) && s.evidence.length > 0 && (
                  <div className="mt-1.5">
                    <div className="text-[9px] opacity-60 font-bold uppercase tracking-wide mb-1">
                      {t.evidence}
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {s.evidence.map((ev, i) => (
                        <span
                          key={i}
                          className={`text-[9px] px-1.5 py-0.5 rounded-md font-mono inline-flex items-center gap-1 ${
                            isDark ? "bg-white/5 text-white/80" : "bg-white text-gray-700 border border-gray-200"
                          }`}
                          data-testid={`auto-diagnosis-ev-${s.condition_id}-${i}`}
                        >
                          <span>
                            {ev.code}: {typeof ev.value === "number" ? ev.value.toFixed(1) : String(ev.value)} ({ev.cutoff})
                          </span>
                          {ev.code && !String(ev.code).startsWith("dutch_flag") && (
                            <MarkerReportButton
                              markerCode={ev.code}
                              currentValue={typeof ev.value === "number" ? ev.value : undefined}
                              size="xs"
                              className="ml-0.5 !px-0.5 !py-0"
                            />
                          )}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* KEGG pathway chips */}
                {Array.isArray(s.matching_pathways) && s.matching_pathways.length > 0 && (
                  <div className="mt-2">
                    <div className="text-[9px] opacity-60 font-bold uppercase tracking-wide mb-1">
                      {t.pathways}
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {s.matching_pathways.slice(0, 6).map((p) => (
                        <a
                          key={p.id}
                          href={p.kegg ? `https://www.kegg.jp/pathway/${p.kegg}` : "#"}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`text-[9px] px-1.5 py-0.5 rounded-md font-medium flex items-center gap-1 ${
                            isDark ? "bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20" :
                                     "bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200"
                          }`}
                          title={p.name_bg}
                        >
                          {p.name_bg}
                          {p.kegg && <ExternalLink className="w-2 h-2 opacity-60" />}
                        </a>
                      ))}
                      {s.matching_pathways.length > 6 && (
                        <span className="text-[9px] opacity-60">
                          +{s.matching_pathways.length - 6}
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {/* ── Already-accepted footer ──────────────────────── */}
      {alreadyAccepted.length > 0 && (
        <div className="mt-3 pt-3 border-t border-dashed border-white/10">
          <div className="text-[10px] opacity-60 font-bold uppercase tracking-wide mb-1">
            {t.alreadyAcceptedTitle}
          </div>
          <div className="flex flex-wrap gap-1" data-testid="auto-diagnosis-already-accepted">
            {alreadyAccepted.map((cid) => (
              <span
                key={cid}
                className={`text-[9px] px-1.5 py-0.5 rounded-md font-mono ${
                  isDark ? "bg-emerald-500/10 text-emerald-300" : "bg-emerald-50 text-emerald-700"
                }`}
              >
                ✓ {cid}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Safety disclaimer + Educational-only ribbon */}
      {hasAny && (
        <>
          <div className={`mt-3 p-2 rounded-lg text-[10px] leading-relaxed flex items-start gap-2 ${
            isDark ? "bg-amber-500/10 text-amber-200/90" : "bg-amber-50 text-amber-800 border border-amber-200"
          }`}>
            <AlertTriangle className="w-3 h-3 shrink-0 mt-0.5" />
            <span>{t.safetyNote}</span>
          </div>
          <EducationalRibbon
            lang={lang}
            variant="inline"
            isDark={isDark}
            className="mt-2"
            testId="auto-diagnosis-educational-ribbon"
          />
        </>
      )}

      {/* iter927 — Inline "Discuss with Bot" dialog. Replaces the previous
          navigate-to-Precision-Health flow (which was confusing to users). */}
      <DiscussBotDialog
        open={discussOpen}
        onOpenChange={setDiscussOpen}
        insight={discussInsight}
        prefillQuery={discussPreQuery}
      />
    </div>
  );
}
